// src/components/ProjectPage.js
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  orderBy,
  query
} from 'firebase/firestore';
import { auth, db, storage } from '../firebaseConfig';
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytesResumable,
  deleteObject
} from 'firebase/storage';
import { useAuthState } from 'react-firebase-hooks/auth';
import generatePdfFromImages from './generatePdfFromImages';
import Sidebar from './Sidebar';
import TileMenu from './TileMenu';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import './Photos.css';
import './Projects.css';

function ProjectPage() {
  const { projectId } = useParams();
  const [projectName, setProjectName] = useState('');
  const [photos, setPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [editingCaption, setEditingCaption] = useState(null);
  const [role, setRole] = useState('engineer');
  const [user] = useAuthState(auth);
  const [isDownloading, setIsDownloading] = useState(false);
  const [subprojects, setSubprojects] = useState([]);
  const [newSubprojectName, setNewSubprojectName] = useState('');
  const [editingSubprojectId, setEditingSubprojectId] = useState(null);
  const [subprojectNameDraft, setSubprojectNameDraft] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const inputRef = useRef();
  const navigate = useNavigate();

  const MAX_BATCH = 30;
  const MAX_TOTAL_BYTES = 200 * 1024 * 1024;

  useEffect(() => {
    if (projectId) {
      fetchProjectData();
      fetchPhotos();
      fetchSubprojects();
    }
  }, [projectId]);

  useEffect(() => {
    const fetchRole = async () => {
      if (user) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const role = userDoc.data().role;
            if (role === 'admin' || role === 'engineer' || role === 'owner') {
              setRole(role);
            }
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
        }
      }
    };
    fetchRole();
  }, [user]);

  const fetchProjectData = async () => {
    try {
      const projectRef = doc(db, 'projects', projectId);
      const projectDoc = await getDoc(projectRef);
      if (projectDoc.exists()) {
        setProjectName(projectDoc.data().name || '');
      }
    } catch (error) {
      console.error('Error fetching project data:', error);
    }
  };

  const fetchPhotos = async () => {
    try {
      const photosRef = collection(db, 'projects', projectId, 'photos');
      const q = query(photosRef, orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);
      const photoList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPhotos(photoList);
    } catch (err) {
      console.error('Error fetching photos:', err);
    }
  };

  const fetchSubprojects = async () => {
    try {
      const subRef = collection(db, 'projects', projectId, 'subprojects');
      const snapshot = await getDocs(subRef);
      const subList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      subList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setSubprojects(subList);
    } catch (err) {
      console.error('Error fetching subprojects:', err);
    }
  };

  const handleCreateSubproject = async () => {
    const name = newSubprojectName.trim();
    if (!name) return;
    try {
      await addDoc(collection(db, 'projects', projectId, 'subprojects'), {
        name,
        createdAt: new Date(),
      });
      setNewSubprojectName('');
      fetchSubprojects();
    } catch (err) {
      console.error('Failed to create subproject:', err);
    }
  };

  const startEditingSubprojectName = (sub) => {
    setSubprojectNameDraft(sub.name || '');
    setEditingSubprojectId(sub.id);
  };

  const commitSubprojectRename = async (subprojectId) => {
    const trimmed = subprojectNameDraft.trim();
    const current = subprojects.find(s => s.id === subprojectId);
    setEditingSubprojectId(null);

    if (!trimmed) return; // reject empty/whitespace-only name
    if ((current?.name || '') === trimmed) return; // unchanged — skip the write

    const previousName = current?.name;
    setSubprojects(prev => prev.map(s => s.id === subprojectId ? { ...s, name: trimmed } : s));
    try {
      await updateDoc(doc(db, 'projects', projectId, 'subprojects', subprojectId), { name: trimmed });
    } catch (err) {
      console.error('Error renaming subproject:', err);
      setSubprojects(prev => prev.map(s => s.id === subprojectId ? { ...s, name: previousName } : s));
      alert('Could not rename subproject.');
    }
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    enqueueFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    enqueueFiles(files);
  };

  const enqueueFiles = (files) => {
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > MAX_TOTAL_BYTES) {
      alert('Total upload size exceeds 200MB. Please select fewer or smaller files.');
      return;
    }
    setUploadQueue(prev => [...prev, ...files]);
  };

  useEffect(() => {
    if (uploadQueue.length > 0) processUploadQueue();
  }, [uploadQueue]);

  const processUploadQueue = async () => {
    const queue = [...uploadQueue];
    setUploadQueue([]);

    for (let i = 0; i < queue.length; i += MAX_BATCH) {
      const batch = queue.slice(i, i + MAX_BATCH);
      await Promise.allSettled(batch.map(uploadSingleFile));
    }

    fetchPhotos();
  };

  const generateThumbnail = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const scale = 500 / img.width;
        canvas.width = 500;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => {
          URL.revokeObjectURL(url);
          resolve(blob);
        }, 'image/jpeg');
      };
      img.onerror = reject;
      img.src = url;
    });
  };

  const uploadSingleFile = async (file) => {
    const filename = file.name;
    const path = `projects/${projectId}/photos/${filename}`;
    const thumbPath = `projects/${projectId}/photos/thumbs/${filename}`;
    const fileRef = storageRef(storage, path);
    const thumbRef = storageRef(storage, thumbPath);

    try {
      const uploadTask = uploadBytesResumable(fileRef, file);
      uploadTask.on('state_changed', snapshot => {
        const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setUploadProgress(prev => ({ ...prev, [filename]: percent }));
      });

      await uploadTask;
      const url = await getDownloadURL(fileRef);

      const thumbBlob = await generateThumbnail(file);
      await uploadBytesResumable(thumbRef, thumbBlob);
      const thumbUrl = await getDownloadURL(thumbRef);

      const caption = filename.replace(/\.[^/.]+$/, '');
      await addDoc(collection(db, 'projects', projectId, 'photos'), {
        url,
        thumbUrl,
        caption,
        filename,
        storagePath: path,
        timestamp: new Date()
      });
    } catch (err) {
      console.error(`Failed to upload ${filename}:`, err);
    }
  };

  const toggleSelection = (photo) => {
    const alreadySelected = selectedPhotos.find((p) => p.id === photo.id);
    if (alreadySelected) {
      setSelectedPhotos(selectedPhotos.filter((p) => p.id !== photo.id));
    } else {
      setSelectedPhotos([...selectedPhotos, photo]);
    }
  };

  const handleCaptionChange = async (photoId, newCaption) => {
    setPhotos((prev) =>
      prev.map((p) =>
        p.id === photoId ? { ...p, caption: newCaption } : p
      )
    );
    try {
      const photoRef = doc(db, 'projects', projectId, 'photos', photoId);
      await updateDoc(photoRef, { caption: newCaption });
    } catch (err) {
      console.error('Error updating caption:', err);
    }
  };

  const handleGeneratePDF = async () => {
    if (selectedPhotos.length === 0) return;
    try {
      const formattedImages = selectedPhotos.map((photo, index) => ({
        url: photo.fullUrl || photo.url,
        name: photo.filename || `IMG_${index + 1}`
      }));

      const captions = {};
      selectedPhotos.forEach((photo) => {
        const key = photo.filename || '';
        captions[key] = photo.caption || key.replace(/\.[^/.]+$/, '');
      });

      await generatePdfFromImages(formattedImages, projectName, captions);
    } catch (error) {
      console.error('PDF generation failed:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handleDeleteSelectedPhotos = async () => {
    if (!window.confirm(`Delete ${selectedPhotos.length} photo(s)? This cannot be undone.`)) return;

    for (const photo of selectedPhotos) {
      try {
        const thumbRef = storageRef(storage, `projects/${projectId}/photos/thumbs/${photo.filename}`);
        await deleteObject(thumbRef).catch(() => {});
        await deleteDoc(doc(db, 'projects', projectId, 'photos', photo.id));
      } catch (err) {
        console.error(`Failed to delete ${photo.filename}:`, err);
      }
    }

    setPhotos(prev => prev.filter(p => !selectedPhotos.some(sp => sp.id === p.id)));
    setSelectedPhotos([]);
    setSelectMode(false);
  };

  const downloadAllPhotos = async () => {
    if (!photos.length) {
      alert("No photos available to download.");
      return;
    }

    setIsDownloading(true);
    const zip = new JSZip();
    const folder = zip.folder(projectName || 'Photos');

    for (const photo of photos) {
      try {
        const fileName = photo.filename || `${photo.id}.jpg`;
        const storagePath = photo.storagePath || `projects/${projectId}/photos/${fileName}`;
        const url = await getDownloadURL(storageRef(storage, storagePath));
        const response = await fetch(url);
        if (!response.ok) continue;
        const blob = await response.blob();
        folder.file(fileName, blob);
      } catch (error) {
        console.error(`Failed to download ${photo.filename}:`, error);
      }
    }

    try {
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${projectName || 'Project'}_Photos.zip`);
      alert('Download complete. Check your Downloads folder.');
    } catch (err) {
      console.error('ZIP generation failed:', err);
      alert('Failed to generate ZIP file.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="ProjectsPage" onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}>
      <header>{projectName || 'Loading project...'}</header>

      <Sidebar
        role={role}
        buttons={[
          {
            label: isDownloading ? 'Zipping Photos...' : 'Download All Photos',
            action: downloadAllPhotos,
            special: true,
            disabled: isDownloading
          },
          {
            label: selectMode ? 'Cancel Selection' : 'Select Photos',
            action: () => setSelectMode(!selectMode),
            special: true
          },
          ...(selectMode
            ? [
                {
                  label: `Generate PDF (${selectedPhotos.length})`,
                  action: handleGeneratePDF,
                  special: true
                },
                {
                  label: `Delete Photos (${selectedPhotos.length})`,
                  action: handleDeleteSelectedPhotos,
                  special: true
                }
              ]
            : [])
        ]}
      />

      <div className="main-content">
        <div className="subproject-input-container">
          <input
            type="text"
            className="subproject-input"
            placeholder="New subproject name"
            value={newSubprojectName}
            onChange={(e) => setNewSubprojectName(e.target.value)}
          />
          <button className="standard-button" onClick={handleCreateSubproject}>
            Create Subproject
          </button>
        </div>

        {subprojects.length > 0 && (
          <div className="project-grid">
            {subprojects.map((sub) => (
              <div
                key={sub.id}
                className="project-card"
                onClick={() => navigate(`/projects/${projectId}/subprojects/${sub.id}`)}
              >
                <TileMenu
                  items={[
                    { key: 'rename', label: 'Rename', onSelect: () => startEditingSubprojectName(sub) }
                  ]}
                  isOpen={openMenuId === sub.id}
                  onOpen={() => setOpenMenuId(sub.id)}
                  onClose={() => setOpenMenuId(null)}
                />
                <div className="thumbnail-placeholder">📁</div>
                {editingSubprojectId === sub.id ? (
                  <input
                    type="text"
                    className="project-name-input"
                    autoFocus
                    value={subprojectNameDraft}
                    onChange={(e) => setSubprojectNameDraft(e.target.value)}
                    onBlur={() => commitSubprojectRename(sub.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitSubprojectRename(sub.id);
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        setEditingSubprojectId(null);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <div className="project-name">{sub.name}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ✅ Spacer between subprojects and upload */}
        <div style={{ height: '2rem' }} />


        <div style={{ marginBottom: '2rem', padding: '1rem', border: '2px dashed #ccc', borderRadius: '8px', background: '#f9f9f9', textAlign: 'center' }}>
          <label htmlFor="photo-upload" style={{ fontWeight: 'bold' }}>Drag & drop or select up to 30 photos:</label>
          <input
            ref={inputRef}
            id="photo-upload"
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileInput}
            style={{ display: 'block', margin: '0.5rem auto' }}
          />
        </div>

        {Object.keys(uploadProgress).length > 0 && (
          <div className="upload-progress-list">
            {Object.entries(uploadProgress).map(([name, progress]) => (
              <div key={name} style={{ marginBottom: '0.5rem' }}>
                <strong>{name}</strong>
                <div className="progress-bar">
                  <div className="progress" style={{ width: `${progress}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="image-container">
          {photos.map((photo) => {
            const selected = selectedPhotos.some((p) => p.id === photo.id);
            const imageElement = (
              <img
                src={photo.thumbUrl?.startsWith('http') ? photo.thumbUrl : photo.url}
                alt={photo.filename}
                className="image-thumb"
                onError={(e) => { e.target.onerror = null; e.target.src = photo.url; }}
              />
            );

            return (
              <div
                key={photo.id}
                className={`image-thumbnail ${selected ? 'selected' : ''}`}
                onClick={() => selectMode && toggleSelection(photo)}
              >
                {selectMode ? (
                  imageElement
                ) : (
                  <a
                    href={photo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {imageElement}
                  </a>
                )}

                <div className="image-caption">
                  {selectMode && (
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleSelection(photo)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  {editingCaption === photo.id ? (
                    <input
                      type="text"
                      autoFocus
                      value={photo.caption || ''}
                      placeholder="Enter caption"
                      onChange={(e) => handleCaptionChange(photo.id, e.target.value)}
                      onBlur={() => setEditingCaption(null)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          setEditingCaption(null);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingCaption(photo.id);
                      }}
                      style={{ cursor: 'text' }}
                    >
                      {photo.caption || photo.filename}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ProjectPage;
